import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Form, Input, Button, Alert, Card, Typography } from 'antd';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import { AuthContext } from '../../context/AuthContext';
import { getDefaultPathForRole } from '../../utils/rolePermissions';
import rlcLogo from '../../assets/RLCI Horizontal Logo Default.png';
import { ACTIVE as RLC } from '../../config/rlcThemeTokens';

const { Title, Text } = Typography;

/**
 * Brand tokens
 */
const BRAND = {
  darkblue: RLC['surface-inverse'],
  blue: RLC['text-accent'],
  text: RLC['text-primary'],
  bg: RLC['surface-sunken'],
  white: RLC['surface'],
  error: RLC['state-error'],
  // "spark." was #ED1C24, a red belonging to no palette. It is the one word
  // meant to catch the eye on this page, which is what Signal Orange is for.
  sparkRed: RLC['action-primary'],
};

/**
 * Canvas ignition animation colours.
 *
 * The animation paints straight onto a <canvas>, so it cannot read CSS
 * variables — every colour below has to be a literal string. Derived from the
 * same tokens so the animation follows the Phase B / Phase C switch instead of
 * staying blue while the rest of the page turns teal.
 */
const SPARKLE = RLC['text-link-inverse'];   // Aquamarine on RLC, white on legacy
const GLOW = BRAND.blue;
const GLOW_FADE = `${GLOW}00`;              // same hue, zero alpha
const GLOW_SOFT = `${GLOW}1a`;
const rgbOf = (hex) => {
  const h = hex.replace('#', '');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)).join(",");
};
const SPARKLE_RGB = rgbOf(SPARKLE);

const HEADLINES = [
  { id: "ideate-collaborate-innovate", label: "Ideate. Collaborate. Innovate." },
  { id: "innovation-at-scale", label: "Innovation, at scale." },
  { id: "ideas-become-innovation", label: "Where ideas become innovation." },
  { id: "accelerate-lifecycle", label: "Accelerate your product lifecycle." },
  { id: "innovative-platform", label: "An innovative platform for you to build new innovations." },
  { id: "best-key-decision", label: "The best Key Decision you will ever make." },
];

/**
 * Events
 */
const SparkEvent = {
  Tease: "spark-tease",
  Fire: "spark-fire",
  Trigger: "spark-trigger",
  Type: "spark-type",
};

/**
 * Helpers
 */
const SAFE_ZONE = { xMin: 0.45, xMax: 0.8, yMin: 0.25, yMax: 0.7 };

function pickRandomCore(width, height) {
  const x = width * (SAFE_ZONE.xMin + Math.random() * (SAFE_ZONE.xMax - SAFE_ZONE.xMin));
  const y = height * (SAFE_ZONE.yMin + Math.random() * (SAFE_ZONE.yMax - SAFE_ZONE.yMin));
  return { x, y };
}

function clampCore(width, height, x, y) {
  const cx = Math.min(Math.max(x, width * SAFE_ZONE.xMin), width * SAFE_ZONE.xMax);
  const cy = Math.min(Math.max(y, height * SAFE_ZONE.yMin), height * SAFE_ZONE.yMax);
  return { x: cx, y: cy };
}

function mix(a, b, t) { return a + (b - a) * t; }
function smoothstep(edge0, edge1, x) {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function outwardUnit(cx, cy, px, py) {
  let dx = px - cx, dy = py - cy;
  let d = Math.hypot(dx, dy);
  if (d < 1e-6) {
    const a = Math.random() * Math.PI * 2;
    return { ux: Math.cos(a), uy: Math.sin(a) };
  }
  return { ux: dx / d, uy: dy / d };
}

/**
 * Main Login Component
 */
export default function Login() {
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const prefersReducedMotion = useReducedMotion();
  const sparkAccent = useMemo(() => BRAND.blue, []);

  // Advance headline on each spark
  useEffect(() => {
    function onFire() { setHeadlineIndex((i) => (i + 1) % HEADLINES.length); }
    window.addEventListener(SparkEvent.Fire, onFire);
    return () => window.removeEventListener(SparkEvent.Fire, onFire);
  }, []);

  return (
    <div
      style={{
        height: '100vh',
        maxHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
        '--brand-darkblue': BRAND.darkblue,
        '--brand-blue': BRAND.blue,
        '--brand-text': BRAND.text,
        '--brand-bg': BRAND.bg,
        '--brand-white': BRAND.white,
        '--brand-error': BRAND.error,
      }}
    >
      {/* Background tint */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: -10,
          background: `radial-gradient(1200px 700px at 85% 15%, var(--rlc-neutral-0), transparent), radial-gradient(900px 600px at 10% 90%, ${BRAND.blue}0f, transparent), ${BRAND.bg}`,
        }}
      />

      <div style={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: '0.3fr 0.7fr',
        height: '100vh',
      }}>
        {/* Left: form */}
        <div
          style={{
            position: 'relative',
            background: BRAND.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
          }}
        >
          <div style={{
            pointerEvents: 'none',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: '1px',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.05), rgba(0,0,0,0.1), rgba(0,0,0,0.05))',
          }} />

          <div style={{ width: '100%', maxWidth: '420px' }}>
            <Card
              style={{
                boxShadow: 'none',
                borderRadius: '8px',
                border: 'none',
              }}
            >
              {/* Logo */}
              <div style={{ marginBottom: '32px', textAlign: 'center' }}>
                <img
                  src={rlcLogo}
                  alt="RLC Logo"
                  style={{
                    maxWidth: '280px',
                    width: '100%',
                    height: 'auto',
                  }}
                />
              </div>

              {/* Title */}
              <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                <Title level={2} style={{ marginBottom: '8px', color: BRAND.text }}>
                  Light the spark
                </Title>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  Log in to move your project from idea to impact
                </Text>
              </div>

              <LoginForm accent={sparkAccent} />

              <div style={{ marginTop: '24px', textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  © {new Date().getFullYear()} Rapid Learning Cycles
                </Text>
              </div>
            </Card>
          </div>
        </div>

        {/* Right: stage */}
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
          }}
          role="img"
          aria-label="Animation stage"
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(1000px 600px at 70% 30%, rgba(255,255,255,0.08), transparent), radial-gradient(800px 500px at 0% 80%, ${BRAND.blue}2e, transparent), ${BRAND.darkblue}`,
            }}
          />
          <CinematicOverlay />

          <IgnitionFusion reduced={prefersReducedMotion} />

          <StageHeadlineOverlay headlineId={HEADLINES[headlineIndex].id} />

          <EdgeHighlights />
        </div>
      </div>
    </div>
  );
}

function LoginForm({ accent }) {
  const navigate = useNavigate();
  const { login, loading, error, currentUser } = useContext(AuthContext);
  const [form] = Form.useForm();
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Navigate when authentication is successful
  useEffect(() => {
    if (loginSuccess && currentUser) {
      // Small delay to show the spark animation before navigating
      const timer = setTimeout(() => {
        // Land on the role's default tool (REQ-001: Core Hypothesis replaces AI Coach)
        navigate(getDefaultPathForRole(currentUser.role), { replace: true });
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [loginSuccess, currentUser, navigate]);

  const handleSubmit = async (values) => {
    try {
      setLoginSuccess(false);
      await login(values.email, values.password);
      // Trigger a spark on successful login
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(SparkEvent.Trigger));
      }
      // Mark login as successful - useEffect will handle navigation
      setLoginSuccess(true);
    } catch (err) {
      setLoginSuccess(false);
      // Error handled by AuthContext
    }
  };

  const handleFieldChange = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(SparkEvent.Type));
    }
  };

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      autoComplete="off"
    >
      <Form.Item
        name="email"
        label="Email"
        rules={[
          { required: true, message: 'Please enter your email' },
          { type: 'email', message: 'Please enter a valid email' }
        ]}
      >
        <Input
          prefix={<MailOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
          placeholder="you@company.com"
          onChange={handleFieldChange}
          autoFocus
        />
      </Form.Item>

      <Form.Item
        name="password"
        label="Password"
        rules={[{ required: true, message: 'Please enter your password' }]}
      >
        <Input.Password
          prefix={<LockOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
          placeholder="••••••••"
          onChange={handleFieldChange}
        />
      </Form.Item>

      <div style={{ marginBottom: '16px', textAlign: 'right' }}>
        <Link to="/forgot-password" style={{ fontSize: '14px', color: BRAND.blue }}>
          Forgot password?
        </Link>
      </div>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      <Form.Item style={{ marginBottom: 0 }}>
        <motion.div
          whileHover={{ y: -2 }}
          whileTap={{ y: 0 }}
          onMouseEnter={() => {
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent(SparkEvent.Tease));
            }
          }}
        >
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            style={{
              fontWeight: 500,
              background: BRAND.blue,
              borderColor: BRAND.blue,
            }}
          >
            {loading ? 'Logging in...' : 'Log in'}
          </Button>
        </motion.div>
      </Form.Item>
    </Form>
  );
}

/**
 * Visual layers
 */
function CinematicOverlay() {
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {/* Soft vignette */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          mixBlendMode: 'overlay',
          backgroundImage: "radial-gradient(ellipse at center, rgba(0,0,0,0) 60%, rgba(0,0,0,0.35) 100%)",
          backgroundSize: "100% 100%",
        }}
      />
      {/* Procedural noise */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <filter id="noiseFilter">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
          </filter>
        </defs>
        <rect width="100%" height="100%" filter="url(#noiseFilter)" opacity="0.035" />
      </svg>
    </div>
  );
}

function EdgeHighlights() {
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.2), transparent)' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.1), transparent)' }} />
    </div>
  );
}

function StageHeadlineOverlay({ headlineId }) {
  const item = HEADLINES.find(h => h.id === headlineId) || HEADLINES[0];
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', padding: '0 1.5rem', maxWidth: '720px', userSelect: 'none' }}>
        <div style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.15em', opacity: 0.8, color: 'rgba(255,255,255,0.8)', marginBottom: '0.5rem' }}>
          Welcome to <span style={{ color: BRAND.sparkRed, fontWeight: 700 }}>spark.</span>
        </div>
        {/* Brand moment — one of the few places Merriweather is used (D-015). */}
        <h2 className="rlc-display" style={{ lineHeight: 1.2, color: 'white', fontSize: '40px', textShadow: '0 2px 20px rgba(0,0,0,0.25)' }}>
          {item.label}
        </h2>
      </div>
    </div>
  );
}

/** IgnitionFusion – particle convergence and spark animation */
function IgnitionFusion({ reduced }) {
  const ref = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d", { alpha: false });
    const dpr = Math.min(2, window.devicePixelRatio || 1);

    let width = (canvas.width = canvas.offsetWidth * dpr);
    let height = (canvas.height = canvas.offsetHeight * dpr);

    let { x: cx, y: cy } = pickRandomCore(width, height);

    const onResize = () => {
      width = canvas.width = canvas.offsetWidth * dpr;
      height = canvas.height = canvas.offsetHeight * dpr;
      const clamped = clampCore(width, height, cx, cy);
      cx = clamped.x; cy = clamped.y;
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(canvas);

    const N = 448;
    const particles = Array.from({ length: N }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.0125,
      vy: (Math.random() - 0.5) * 0.0125,
    }));

    let absorbed = 0;
    let phase = 0;
    let timer = 0;
    let teaseCooldown = 0;
    let exhaleFrames = 0;
    let calmFrames = 0;

    let baseCore = 3;
    let coreR = baseCore;
    const maxCore = 16;

    function absorbParticle(i) {
      particles.splice(i, 1);
      absorbed++;
    }

    function resetCycle() {
      while (particles.length < N) {
        const p = {
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.0125,
          vy: (Math.random() - 0.5) * 0.0125,
        };
        const dx = p.x - cx, dy = p.y - cy;
        if (dx*dx + dy*dy < (coreR + 30) * (coreR + 30)) continue;
        particles.push(p);
      }
      ({ x: cx, y: cy } = pickRandomCore(width, height));
      baseCore = 3;
      coreR = baseCore;
      absorbed = 0;
    }

    function handleTease(){ if (reduced) return; teaseCooldown = 30; }
    function handleTrigger(){ if (reduced) return; phase = 3; timer = 0; }
    function handleType(){ if (reduced) return; calmFrames = Math.min(140, calmFrames + 35); }

    window.addEventListener(SparkEvent.Tease, handleTease);
    window.addEventListener(SparkEvent.Trigger, handleTrigger);
    window.addEventListener(SparkEvent.Type, handleType);

    const rings = [];

    function loop() {
      ctx.fillStyle = BRAND.darkblue;
      ctx.fillRect(0, 0, width, height);

      const bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, 300);
      bg.addColorStop(0, GLOW_SOFT);
      bg.addColorStop(1, GLOW_FADE);
      ctx.fillStyle = bg;
      ctx.beginPath(); ctx.arc(cx, cy, 340, 0, Math.PI*2); ctx.fill();

      if (!reduced) timer++;
      if (phase === 0 && timer > 420) { phase = 1; timer = 0; ({ x: cx, y: cy } = pickRandomCore(width, height)); }
      if (phase === 1 && timer > 900) { phase = 2; timer = 0; }
      if (phase === 3 && timer > 28)  { phase = 4; timer = 0; exhaleFrames = 0; }
      if (phase === 4 && exhaleFrames > 420) { phase = 0; timer = 0; resetCycle(); }

      const calm = Math.min(1, calmFrames / 140); if (calmFrames > 0) calmFrames--;
      const progress = Math.min(1, absorbed / N);
      const endAccel = smoothstep(0.8, 1.0, progress);
      coreR = mix(baseCore, maxCore, progress);
      const G = mix(12, 120, endAccel);
      const SWIRL = mix(0.05, 0.6, endAccel);
      const DRAG = 0.9993 + 0.0007 * calm;

      const absorbR = Math.max(10, coreR - 6);
      const absorbR2 = absorbR * absorbR;

      ctx.globalCompositeOperation = "lighter";
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        let ax = 0, ay = 0;

        if (phase === 0) {
          ax += (Math.sin(p.y * 0.0012) - 0.5) * 0.0015;
          ay += (Math.cos(p.x * 0.0012) - 0.5) * 0.0015;
        } else if (phase === 1 || phase === 2) {
          const dx = cx - p.x, dy = cy - p.y;
          const r2 = Math.max(900, dx*dx + dy*dy);
          const inv = 1 / r2;
          ax += dx * G * inv;
          ay += dy * G * inv;
          ax += (-dy) * (SWIRL * inv);
          ay += ( dx) * (SWIRL * inv);
          {
            const d = Math.max(1, Math.hypot(dx, dy));
            const ex = dx / d, ey = dy / d;
            const vr = p.vx * ex + p.vy * ey;
            const vx_t = p.vx - vr * ex;
            const vy_t = p.vy - vr * ey;
            p.vx -= vx_t * 0.08;
            p.vy -= vy_t * 0.08;
          }
        } else if (phase === 4) {
          ax += (Math.sin(p.y * 0.008 + exhaleFrames * 0.02)) * 0.003;
          ay += (Math.cos(p.x * 0.008 + exhaleFrames * 0.018)) * 0.003;
        }

        if (teaseCooldown > 0) {
          const dx = cx - p.x, dy = cy - p.y; const d = Math.max(1, Math.hypot(dx, dy));
          ax += (dx / d) * 0.0015; ay += (dy / d) * 0.0015;
        }

        p.vx = (p.vx + ax) * DRAG; p.vy = (p.vy + ay) * DRAG;
        p.x += p.vx; p.y += p.vy;

        const ddx = p.x - cx, ddy = p.y - cy; const d2 = ddx*ddx + ddy*ddy;
        if ((phase === 1 || phase === 2) && d2 <= absorbR2) {
          absorbParticle(i);
          continue;
        }

        if (phase === 4) {
          exhaleFrames++;
          if (p.x < -20 || p.x > width + 20 || p.y < -20 || p.y > height + 20) {
            p.x = Math.random() * width; p.y = Math.random() * height; p.vx *= 0.2; p.vy *= 0.2;
          }
        } else {
          if (p.x < 0 || p.x > width) p.vx *= -0.9; if (p.y < 0 || p.y > height) p.vy *= -0.9;
        }

        const lg = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 3);
        lg.addColorStop(0, SPARKLE); lg.addColorStop(1, GLOW_FADE);
        ctx.fillStyle = lg; ctx.beginPath(); ctx.arc(p.x, p.y, 3, 0, Math.PI*2); ctx.fill();
      }

      if (phase >= 1) {
        const fullness = Math.min(1, (coreR - baseCore) / (maxCore - baseCore));
        const shakeAmp = 1 + 3 * Math.pow(smoothstep(0.9, 1, fullness), 2);
        const sx = (Math.random() - 0.5) * shakeAmp;
        const sy = (Math.random() - 0.5) * shakeAmp;
        const coreG = ctx.createRadialGradient(cx + sx, cy + sy, 0, cx + sx, cy + sy, 120);
        coreG.addColorStop(0, BRAND.white); coreG.addColorStop(1, GLOW_FADE);
        ctx.fillStyle = coreG; ctx.beginPath(); ctx.arc(cx + sx, cy + sy, coreR, 0, Math.PI*2); ctx.fill();
      }

      if ((phase === 1 || phase === 2) && particles.length === 0) { phase = 3; timer = 0; }

      if (phase === 3) {
        try { window.dispatchEvent(new CustomEvent(SparkEvent.Fire)); } catch {}
        const releaseCount = Math.max(0, N - particles.length);
        for (let k = 0; k < releaseCount; k++) {
          particles.push({ x: cx, y: cy, vx: 0, vy: 0 });
        }
        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          const { ux, uy } = outwardUnit(cx, cy, p.x, p.y);
          const speed = 6 + Math.random() * 3;
          p.vx = ux * speed; p.vy = uy * speed;
        }
        for (let i = 0; i < 44; i++) {
          const a = (i / 44) * Math.PI * 2; const len = 90 + Math.random() * 70;
          ctx.strokeStyle = "#ffffff"; ctx.lineWidth = 1.2;
          ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx + Math.cos(a) * len, cy + Math.sin(a) * len); ctx.stroke();
        }
        const absorbTo = coreR;
        rings.push({ r: absorbTo + 120, life: 22, inward: true, absorbTo });
        coreR = baseCore;
        phase = 4; timer = 0; exhaleFrames = 0;
      }

      for (let i = rings.length - 1; i >= 0; i--) {
        const s = rings[i];
        const alpha = Math.max(0, Math.min(1, s.life / 22));
        if (s.inward) {
          s.r -= 10;
          s.life -= 1;
          ctx.strokeStyle = `rgba(${SPARKLE_RGB},${0.6 * alpha})`;
          ctx.lineWidth = 1.4;
          ctx.beginPath(); ctx.arc(cx, cy, Math.max(s.r, s.absorbTo), 0, Math.PI*2); ctx.stroke();
          if (s.r <= s.absorbTo + 1 || s.life <= 0) { rings.splice(i, 1); }
        }
      }

      if (teaseCooldown > 0) teaseCooldown--;

      ctx.globalCompositeOperation = "source-over";
      rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      window.removeEventListener(SparkEvent.Tease, handleTease);
      window.removeEventListener(SparkEvent.Trigger, handleTrigger);
      window.removeEventListener(SparkEvent.Type, handleType);
    };
  }, [reduced]);

  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />;
}
